import { LanguageCodeEnum } from '@systemeio/ui'
import ContentSkeleton from 'modules/stores/store/components/content-skeleton'
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { addLanguageResource } from 'shared/components/language-switcher/utils/add-language-resource'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface StoreLocoWrapperProps {
  languageCode: LanguageCodeEnum
}

/**
 * You should wrap the content of the store with this component if you will use store's locale for initial values.
 */
const StoreLocoWrapper: FC<PropsWithChildren<StoreLocoWrapperProps>> = ({
  languageCode,
  children,
}) => {
  const { i18n } = useLocoTranslation()

  // i18n doesn't trigger re-render when locale is changed, so we need to force update
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    if (!languageCode) return
    // for showing default values in stores language
    const loadTranslations = async () => {
      await addLanguageResource(languageCode, 'store', i18n)
      forceUpdate()
    }

    loadTranslations()
  }, [forceUpdate, i18n, languageCode])

  const hasLocale = languageCode !== undefined && i18n.hasResourceBundle(languageCode, 'store')

  return hasLocale ? <>{children}</> : <ContentSkeleton />
}

export default StoreLocoWrapper

const useForceUpdate = () => {
  const [, setState] = useState(0)
  return useCallback(() => setState(state => state + 1), [])
}
